import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../api/axios';
import { normalizeHiringSignalMessage } from '../../utils/userFacingMessages';

export const fetchHiringSignals = createAsyncThunk('hiringSignals/fetchHiringSignals', async (_, thunkAPI) => {
    try {
        const response = await api.get(`/api/hsignal/hiringsignals`);

        return response.data;
    } catch (error) {
        return thunkAPI.rejectWithValue(error.message || 'Failed to fetch signals');
    }
}
);

export const fetchGrowthSignals = createAsyncThunk('hiringSignals/fetchGrowthSignals', async (_, thunkAPI) => {
    try {
        const response = await api.get(`/api/hsignal/growthsignals`);

        return response.data;
    } catch (error) {
        return thunkAPI.rejectWithValue(error.message || 'Failed to fetch growth signals');
    }
});

export const fetchPerplexitySignals = createAsyncThunk("hiringSignals/fetchPerplexitySignals", async ({ limit = 20, skip = 0, force = false } = {}, thunkAPI) => {
    try {
        console.log(`📊 [fetchPerplexitySignals] Fetching signals: limit=${limit}, skip=${skip}, force=${force}`);
        // PROGRESSIVE LOADING: Start with 20 signals for instant display
        // Always request newest first so the backend sort matches the frontend default
        const response = await api.get(`/api/hsignal/perplexitysignals?limit=${limit}&skip=${skip}&sortBy=createdAt&order=desc&force=${force ? 'true' : 'false'}`);
        console.log('✅ Perplexity signals response:', response.data?.length || 0, 'signals');
        return response.data;
    } catch (error) {
        console.error('❌ Failed to fetch perplexity signals:', error);
        return thunkAPI.rejectWithValue(error.response?.data?.message || error.message || 'Failed to fetch perplexity signals');
    }
})

export const fetchCompanyOverview = createAsyncThunk('hiringSignals/fetchCompanyOverview', async (companyId, thunkAPI) => {
    try {
        const response = await api.get(`/api/hsignal/company/${companyId}/overview`)

        return response.data;
    } catch (error) {
        return thunkAPI.rejectWithValue(error.message || 'Failed to fetch company overview');
    }
})

// AI-generated insights for a hiring-signal company (cached server-side; pass
// refresh:true to regenerate). The Company Insights panel consumes this directly.
/*
 * `includePositioning` is off by default.
 *
 * Company research is generated once per company and shared by everyone;
 * positioning is a second, per-user AI call. Leaving it on meant every reader
 * paid for advice they might never scroll to, so it is now requested only by
 * the button that shows it.
 */
export const fetchCompanyInsights = createAsyncThunk('hiringSignals/fetchCompanyInsights', async ({ companyName, organizationId = null, companyId = null, ticker = null, refresh = false, includePositioning = false }, thunkAPI) => {
    try {
        // `ticker` is what resolves this company to its SEC filings server-side.
        // Without it the insights fall back to our own stored description, which
        // is thinner but never invented — so it is optional, not required.
        const response = await api.post(`/api/hsignal/company-insights${refresh ? '?refresh=1' : ''}`, { companyName, organizationId, companyId, ticker, refresh, includePositioning });
        return response.data;
    } catch (error) {
        return thunkAPI.rejectWithValue(error.response?.data?.message || 'Failed to generate company insights');
    }
})

/**
 * Positioning that has ALREADY been generated for this user at this company.
 *
 * Never reaches a model, so it is safe on every page load — it is what lets the
 * page show saved advice instead of the button that would pay for it again.
 */
export const peekCompanyPositioning = createAsyncThunk('hiringSignals/peekCompanyPositioning', async ({ companyName, organizationId = null, companyId = null }, thunkAPI) => {
    try {
        const response = await api.post('/api/hsignal/company-positioning/peek', { companyName, organizationId, companyId });
        return response.data;
    } catch (error) {
        return thunkAPI.rejectWithValue(error.response?.data?.message || 'Failed to read positioning');
    }
});

export const fetchUniqueDepartments = createAsyncThunk("hiringSignals/fetchUniqueDepartments", async (_, thunkAPI) => {
    try {
        const response = await api.get(`/api/hsignal/hsdepartments`);

        return response.data;
    } catch (error) {
        return thunkAPI.rejectWithValue(error.message || 'Failed to fetch departments');
    }
})

export const startSignalScan = createAsyncThunk("hiringSignals/startSignalScan", async ({ preferredIndustry, preferredDepartment }, thunkAPI) => {
    try {
        // This immediately dispatches the 'startScan' action.
        thunkAPI.dispatch(startScan());
        const token = localStorage.getItem('token');

        const response = await api.post("/api/recruitercontacts/startsignalscan",
            { preferredIndustry, preferredDepartment },
            {
                headers: {
                    "auth-token": token
                }
            }
        )

        return response.data;
    } catch (error) {
        const message = error.response?.data?.message || error.message;
        thunkAPI.dispatch(scanFailed({ message }));
        return thunkAPI.rejectWithValue(message);
    }
})

export const fetchNewHiringSignals = createAsyncThunk("hiringSignals/fetchNewHiringSignals", async (filters = {}, thunkAPI) => {
    try {
        console.log('🔍 Fetching new hiring signals...', filters);
        // filters: { location, employeeSize, fundingStage } — optional; backend applies profile fallbacks.
        const response = await api.post(`/api/hsignal/fetch-new-signals`, filters || {});
        console.log('✅ New hiring signals response:', response.data);
        
        // Update user credits in auth slice
        if (response.data.newCreditsBalance !== undefined) {
            thunkAPI.dispatch({ 
                type: 'auth/updateCredits', 
                payload: response.data.newCreditsBalance 
            });
        }
        
        return response.data;
    } catch (error) {
        console.error('❌ Failed to fetch new hiring signals:', error);
        return thunkAPI.rejectWithValue(normalizeHiringSignalMessage(error));
    }
})

const hiringSignalsSlice = createSlice({
    name: 'hiringSignals',
    initialState: {
        hiringSignals: [],
        growthSignals: [],
        perplexitySignals: [],
        companyOverview: [],
        departments: [],
        progressMessage: '',
        loading: false,
        status: 'idle',
        error: null,
    },
    reducers: {
        startScan: (state) => {
            state.status = 'scanning';
            state.perplexitySignals = [];
            state.error = null;
            state.progressMessage = 'Scan initiated...';
        },
        updateProgress: (state, action) => {
            state.progressMessage = action.payload.message;
        },
        addSignal: (state, action) => {
            const exists = state.perplexitySignals.some(signal => signal._id === action.payload._id);
            if (!exists) {
                state.perplexitySignals.push(action.payload);
            }
        },
        scanComplete: (state, action) => {
            state.status = 'complete';
            const signalsFound = Number(action.payload?.stats?.signalsFound || 0);
            const fallbackSignalsFound = Number(action.payload?.stats?.fallbackSignalsFound || 0);
            if (action.payload?.message) {
                state.progressMessage = action.payload.message;
            } else if (signalsFound > 0) {
                state.progressMessage = `${signalsFound} hiring signal${signalsFound === 1 ? '' : 's'} added.${fallbackSignalsFound > 0 ? ' These are best-available matches, so no credits were used.' : ''}`;
            } else {
                state.progressMessage = 'Scan complete. No new hiring signals were found this time.';
            }
        },
        scanFailed: (state, action) => {
            state.status = 'error';
            state.error = action.payload.message;
            state.progressMessage = action.payload.message;
        },
        // Set demo data for onboarding
        setDemoData: (state, action) => {
            state.perplexitySignals = action.payload;
            state.loading = false;
            state.error = null;
            state.status = 'complete';
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchHiringSignals.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchHiringSignals.fulfilled, (state, action) => {
                state.loading = false;
                state.hiringSignals = action.payload;
            })
            .addCase(fetchHiringSignals.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })

            .addCase(fetchGrowthSignals.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchGrowthSignals.fulfilled, (state, action) => {
                state.loading = false;
                state.growthSignals = action.payload;
            })
            .addCase(fetchGrowthSignals.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })

            // --- Perplexity Signals Cases ---
            .addCase(fetchPerplexitySignals.pending, (state, action) => {
                // Only show loading on first fetch (skip=0)
                const isInitialFetch = action.meta?.arg?.skip === 0 || !action.meta?.arg;
                if (isInitialFetch) {
                    state.loading = true;
                }
            })
            .addCase(fetchPerplexitySignals.fulfilled, (state, action) => {
                state.loading = false;
                const skip = action.meta?.arg?.skip || 0;
                
                // PROGRESSIVE LOADING: Append if skip > 0, replace if skip = 0
                if (skip === 0) {
                    // Initial load: replace signals
                    state.perplexitySignals = action.payload || [];
                    console.log('📦 [Redux] Initial signals loaded:', state.perplexitySignals.length);
                } else {
                    // Append mode: add to existing signals (with deduplication)
                    const existingIds = new Set(state.perplexitySignals.map(s => s._id));
                    const newSignals = (action.payload || []).filter(s => !existingIds.has(s._id));
                    state.perplexitySignals = [...state.perplexitySignals, ...newSignals];
                    console.log(`📦 [Redux] Appended ${newSignals.length} signals. Total: ${state.perplexitySignals.length}`);
                }
            })
            .addCase(fetchPerplexitySignals.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
                console.error('❌ [Redux] Failed to fetch signals:', action.payload);
            })

            .addCase(fetchCompanyOverview.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchCompanyOverview.fulfilled, (state, action) => {
                state.loading = false;
                state.companyOverview = action.payload;
            })
            .addCase(fetchCompanyOverview.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })

            .addCase(fetchUniqueDepartments.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchUniqueDepartments.fulfilled, (state, action) => {
                state.loading = false;
                state.departments = action.payload;
            })
            .addCase(fetchUniqueDepartments.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })

            // --- Fetch New Hiring Signals Cases ---
            .addCase(fetchNewHiringSignals.pending, (state) => {
                state.loading = true;
                state.status = 'scanning';
                state.progressMessage = 'Searching for new hiring signals...';
                state.error = null;
            })
            .addCase(fetchNewHiringSignals.fulfilled, (state, action) => {
                state.loading = false;
                state.perplexitySignals = action.payload.signals || state.perplexitySignals;
                if (action.payload.backgroundScanStarted) {
                    state.status = 'scanning';
                    state.progressMessage = action.payload.message || 'Scanning for new hiring signals. This may take a few moments...';
                    state.error = null;
                    return;
                }
                state.status = 'complete';
                if (action.payload.newSignalsCount > 0) {
                    state.progressMessage = `Found ${action.payload.newSignalsCount} new hiring signal${action.payload.newSignalsCount !== 1 ? 's' : ''}!`;
                } else {
                    state.progressMessage = 'No new hiring signals were found this time.';
                }
                state.error = null;
            })
            .addCase(fetchNewHiringSignals.rejected, (state, action) => {
                state.loading = false;
                state.status = 'error';
                state.error = action.payload?.message || action.payload;
                state.progressMessage = action.payload?.message || action.payload;
            });
    },
});


export const { startScan, updateProgress, addSignal, scanComplete, scanFailed } = hiringSignalsSlice.actions;

export default hiringSignalsSlice.reducer;
